import React, { useState } from 'react';
import {
    Button,
    CheckBox,
    Group,
    Heading,
    Level,
    Logo,
    Text,
    TextType,
    useTheme,
    Variant,
} from '@defa/defa-component-library';
import { useSearchParams } from 'react-router-dom';
import { BrandPanel } from '@fractions/brand-panel';
import i18n from '@i18n';
import { Loading } from '@screens/loading';
import {
    ActionRow,
    BrandContent,
    BrandSide,
    Chip,
    ChipList,
    MainContent,
    MainSide,
    MobileHeader,
    Root,
    Section,
} from './rfid-invite.styles';
import { formatPhoneNumber } from '@utils/phone-number';
import {
    useAcceptRfidInviteMutation,
    useGetRfidInviteQuery,
    useRejectRfidInviteMutation,
} from '@generated/rfid-invite/@tanstack/react-query.gen';

function joinName(firstName?: string, lastName?: string) {
    return [firstName, lastName].join(' ').trim();
}

export const RfidInvite: React.FunctionComponent = () => {
    const theme = useTheme();
    const [query] = useSearchParams();
    const token = query.get('token');
    const [haveRead, setHaveRead] = useState(false);

    const invite = useGetRfidInviteQuery({ path: { token } });
    const accept = useAcceptRfidInviteMutation();
    const reject = useRejectRfidInviteMutation();

    if (invite.isLoading) {
        return <Loading />;
    }

    return (
        <Root>
            <BrandSide>
                <BrandContent>
                    <Group>
                        <BrandPanel
                            heading={i18n.t('BrandPanel.Header')}
                            subHeading={i18n.t('BrandPanel.SubHeader')}
                        />
                    </Group>
                </BrandContent>
            </BrandSide>
            <MainSide>
                <MainContent>
                    <MobileHeader>
                        <Logo variant="cloudcharge" size={36} />
                        <Heading level={Level.h1}>{i18n.t('BrandPanel.Header')}</Heading>
                    </MobileHeader>
                    <Section>
                        <Heading level={Level.h1}>{i18n.t('RfidInvite.Header')}</Heading>
                        <Text>{i18n.t('RfidInvite.SubHeader')}</Text>
                    </Section>

                    {!token && (
                        <Section>
                            <Text type={TextType.body} color={theme.foreground.error}>
                                {i18n.t('RfidInvite.MissingToken')}
                            </Text>
                        </Section>
                    )}
                    {invite.error && (
                        <Section>
                            <Text type={TextType.body} color={theme.foreground.error}>
                                {isNotFound(invite.error.message)
                                    ? i18n.t('RfidInvite.CouldNotFindRfid')
                                    : invite.error.message}
                            </Text>
                        </Section>
                    )}
                    {(accept.error || reject.error) && (
                        <Section>
                            <Text type={TextType.body} color={theme.foreground.error}>
                                {(accept || reject).error.message}
                            </Text>
                        </Section>
                    )}

                    {(accept.isSuccess || reject.isSuccess) && (
                        <Section>
                            <Text type={TextType.descriptionBold}>
                                {accept.isSuccess
                                    ? i18n.t('RfidInvite.AcceptSuccess')
                                    : i18n.t('RfidInvite.RejectSuccess')}
                            </Text>
                        </Section>
                    )}

                    {invite.isSuccess && !accept.isSuccess && !reject.isSuccess && (
                        <>
                            <Section>
                                <Heading level={Level.h4}>{i18n.t('Name')}</Heading>
                                <Text type={TextType.body}>
                                    {joinName(invite.data.firstName, invite.data.lastName)}
                                </Text>
                            </Section>
                            <Section>
                                <Heading level={Level.h4}>
                                    {i18n.t('RfidInvite.PhoneNumberLabel')}
                                </Heading>
                                <Text type={TextType.body}>
                                    {formatPhoneNumber(invite.data.phoneNumber)}
                                </Text>
                            </Section>

                            <Section>
                                <Heading level={Level.h4}>
                                    {i18n.t('RfidInvite.Organizations')}
                                </Heading>
                                <ChipList>
                                    {invite.data.organizationNames.length > 0 ? (
                                        invite.data.organizationNames.map((organizationName) => (
                                            <Chip key={organizationName}>{organizationName}</Chip>
                                        ))
                                    ) : (
                                        <Text type={TextType.body}>
                                            {i18n.t('RfidInvite.NoOrganizations')}
                                        </Text>
                                    )}
                                </ChipList>
                            </Section>

                            <Section>
                                <Heading level={Level.h4}>
                                    {i18n.t('RfidInvite.RfidCardsLabel')}
                                </Heading>
                                <ChipList>
                                    {invite.data.rfidCards.length > 0 ? (
                                        invite.data.rfidCards.map((rfidCard) => (
                                            <Chip key={rfidCard}>{rfidCard}</Chip>
                                        ))
                                    ) : (
                                        <Text type={TextType.body}>
                                            {i18n.t('RfidInvite.NoRfidCards')}
                                        </Text>
                                    )}
                                </ChipList>
                            </Section>

                            <Section>
                                <CheckBox
                                    checked={haveRead}
                                    onChange={() => setHaveRead(!haveRead)}
                                    label={i18n.t('RfidInvite.AcceptInviteText')}
                                    name="accept"
                                />
                            </Section>

                            <ActionRow>
                                <Button
                                    text={i18n.t('RfidInvite.AcceptButton')}
                                    onClick={() =>
                                        accept.mutate({ path: { token: invite.data.token } })
                                    }
                                    disabled={accept.isPaused || !haveRead || reject.isPending}
                                    loading={accept.isPending}
                                />
                                <Button
                                    text={i18n.t('RfidInvite.RejectButton')}
                                    variant={Variant.GRAYSCALE}
                                    onClick={() =>
                                        reject.mutate({ path: { token: invite.data.token } })
                                    }
                                    disabled={reject.isPending || accept.isPending}
                                    loading={reject.isPending}
                                />
                            </ActionRow>
                        </>
                    )}
                </MainContent>
            </MainSide>
        </Root>
    );
};

function isNotFound(error: string) {
    return error.toLowerCase().includes('rfid invite not found');
}
